import { Select, type SelectOption, type SelectSize } from '@trustoo/components/Select/v2'
import React from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

// ---------- Select (v2) showcase ----------
// Generated from Figma's "Select" set (5:22375). The design-system form select /
// combobox: Radix-based, data-driven `options`, six types (default · icon · avatar
// · dot leading · search · tags). Rendered uncontrolled here — each Select manages
// its own value, so the gallery is live without page-level state.

const SELECT_COUNTRIES: SelectOption[] = [
    { value: 'nl', label: 'Netherlands' },
    { value: 'de', label: 'Germany' },
    { value: 'be', label: 'Belgium' },
    { value: 'es', label: 'Spain' },
    { value: 'us', label: 'United States' },
]

const SELECT_TEAM: SelectOption[] = [
    { value: 'olivia', label: 'Olivia Rhye', supportingText: '@olivia', avatarInitials: 'OR' },
    { value: 'phoenix', label: 'Phoenix Baker', supportingText: '@phoenix', avatarInitials: 'PB' },
    { value: 'lana', label: 'Lana Steiner', supportingText: '@lana', avatarInitials: 'LS' },
]

const SELECT_STATUS: SelectOption[] = [
    { value: 'active', label: 'Active', dotClassName: 'bg-green-500' },
    { value: 'away', label: 'Away', dotClassName: 'bg-amber-500' },
    { value: 'offline', label: 'Offline', dotClassName: 'bg-neutral-400' },
]

const SELECT_SKILLS: SelectOption[] = [
    { value: 'design', label: 'Design' },
    { value: 'research', label: 'Research' },
    { value: 'frontend', label: 'Frontend' },
    { value: 'backend', label: 'Backend' },
]

const SELECT_USAGE = `import { Select } from '@trustoo/components/Select/v2'

// Single-select (uncontrolled; pass value + onChange to control it)
<Select
    label="Country"
    placeholder="Select a country"
    options={[{ value: 'nl', label: 'Netherlands' }, { value: 'de', label: 'Germany' }]}
    onChange={setCountry}
/>

// Leading adornments per type
<Select type="icon-leading" options={options} />
<Select type="avatar-leading" options={team} />     // option.avatarSrc / avatarInitials
<Select type="dot-leading" options={status} />      // option.dotClassName

// Searchable typeahead
<Select type="search" options={options} placeholder="Search..." />

// Multi-select chips
<Select type="tags" options={skills} defaultValues={['design']} onChangeMultiple={setSkills} />

// Label / validation
<Select label="Country" required helpIcon hintText="Pick one" options={options} />
<Select label="Country" error="Please choose a valid country." options={options} />`

export const SelectShowcase: React.FC = () => (
    <Section
        title="Select (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Select</span> set
                (ComponentSet <InlineCode>5:22375</InlineCode>). The design-system form select /
                combobox, built on <InlineCode>@radix-ui/react-select</InlineCode> (single-select)
                and <InlineCode>@radix-ui/react-popover</InlineCode> (search + tags), driven by a
                data <InlineCode>options</InlineCode> array. The field shell mirrors{' '}
                <InlineCode>Input/v2</InlineCode>. Import v2 via{' '}
                <InlineCode>@trustoo/components/Select/v2</InlineCode> (net-new — no v1 barrel yet).
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">Types</h3>
                <p className="mb-spacing-lg text-xs text-text-tertiary">
                    Each <span className="font-semibold">type</span> changes the leading adornment
                    and interaction. All are live — open them and pick a value.
                </p>
                <div className="flex flex-wrap gap-x-spacing-4xl gap-y-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <div className="w-72 max-w-full">
                        <Select
                            type="default"
                            label="Country"
                            placeholder="Select a country"
                            options={SELECT_COUNTRIES}
                            hintText="default"
                        />
                    </div>
                    <div className="w-72 max-w-full">
                        <Select
                            type="icon-leading"
                            label="Country"
                            placeholder="Select a country"
                            options={SELECT_COUNTRIES}
                            hintText="icon-leading"
                        />
                    </div>
                    <div className="w-72 max-w-full">
                        <Select
                            type="avatar-leading"
                            label="Assignee"
                            defaultValue="olivia"
                            options={SELECT_TEAM}
                            hintText="avatar-leading"
                        />
                    </div>
                    <div className="w-72 max-w-full">
                        <Select
                            type="dot-leading"
                            label="Status"
                            defaultValue="active"
                            options={SELECT_STATUS}
                            hintText="dot-leading"
                        />
                    </div>
                    <div className="w-72 max-w-full">
                        <Select
                            type="search"
                            label="Search country"
                            placeholder="Search..."
                            options={SELECT_COUNTRIES}
                            hintText="search (typeahead)"
                        />
                    </div>
                    <div className="w-72 max-w-full">
                        <Select
                            type="tags"
                            label="Skills"
                            defaultValues={['design', 'frontend']}
                            options={SELECT_SKILLS}
                            hintText="tags (multi-select)"
                        />
                    </div>
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    Sizes &amp; states
                </h3>
                <div className="grid gap-spacing-xl sm:grid-cols-2">
                    {(['sm', 'md'] as SelectSize[]).map((size) => (
                        <div
                            key={size}
                            className="space-y-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                            <div className="flex items-baseline gap-spacing-md">
                                <span className="text-sm font-semibold text-text-primary">
                                    {size === 'sm' ? 'Small' : 'Medium'}
                                </span>
                                <InlineCode>size=&quot;{size}&quot;</InlineCode>
                            </div>
                            <div className="w-72 max-w-full space-y-spacing-xl">
                                <Select
                                    size={size}
                                    label="Country"
                                    placeholder="Select a country"
                                    options={SELECT_COUNTRIES}
                                    hintText="Placeholder"
                                />
                                <Select
                                    size={size}
                                    label="Country"
                                    defaultValue="nl"
                                    options={SELECT_COUNTRIES}
                                    hintText="Filled"
                                />
                                <Select
                                    size={size}
                                    label="Country"
                                    placeholder="Select a country"
                                    options={SELECT_COUNTRIES}
                                    required
                                    helpIcon
                                    hintText="Required + help icon"
                                />
                                <Select
                                    size={size}
                                    label="Country"
                                    placeholder="Select a country"
                                    options={SELECT_COUNTRIES}
                                    disabled
                                    hintText="Disabled"
                                />
                                <Select
                                    size={size}
                                    label="Country"
                                    defaultValue="de"
                                    options={SELECT_COUNTRIES}
                                    error="Please choose a valid country."
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={SELECT_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>options</InlineCode> (
                    <InlineCode>
                        {
                            '{ value, label, supportingText?, disabled?, iconLeading?, avatarSrc?, avatarInitials?, dotClassName? }'
                        }
                    </InlineCode>
                    ), <InlineCode>type</InlineCode> (default · icon-leading · avatar-leading ·
                    dot-leading · search · tags), <InlineCode>size</InlineCode> (sm · md), single{' '}
                    <InlineCode>value</InlineCode> / <InlineCode>defaultValue</InlineCode> /{' '}
                    <InlineCode>onChange</InlineCode> or multi <InlineCode>values</InlineCode> /{' '}
                    <InlineCode>defaultValues</InlineCode> /{' '}
                    <InlineCode>onChangeMultiple</InlineCode>, plus <InlineCode>label</InlineCode>,{' '}
                    <InlineCode>hintText</InlineCode>, <InlineCode>required</InlineCode>,{' '}
                    <InlineCode>helpIcon</InlineCode>, <InlineCode>disabled</InlineCode>,{' '}
                    <InlineCode>destructive</InlineCode> and <InlineCode>error</InlineCode>.
                </p>
            </div>
        </div>
    </Section>
)
